<template>
  <div class="tii">
    <label v-if="label" class="tii__label">{{ label }}</label>
    <p v-if="helpText" class="tii__help">{{ helpText }}</p>

    <!-- Drop zone: doubles as preview when an image is set, drop target
         and click-to-browse when empty. Click on the empty state opens
         the picker; click on a preview opens replace. The hover overlay
         on a preview surfaces the destructive Remove + replace actions. -->
    <div
      :class="[
        'tii__zone',
        {
          'tii__zone--has-image': hasImage,
          'tii__zone--dragover': isDragOver,
          'tii__zone--uploading': uploading,
          'tii__zone--error': !!errorMessage,
          'tii__zone--focal-mode': focalEnabled && hasImage,
          'tii__zone--focal-dragging': isDraggingFocal,
        },
      ]"
      role="button"
      tabindex="0"
      :aria-label="
        focalEnabled && hasImage
          ? 'Click to set focal point'
          : (hasImage ? 'Replace image' : 'Upload image')
      "
      @click="onZoneClick"
      @pointerdown="onFocalPointerDown"
      @keydown.enter.prevent="onZoneClick"
      @keydown.space.prevent="onZoneClick"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <!-- Image preview -->
      <img
        v-if="hasImage"
        :src="modelValue"
        :alt="label || 'Preview'"
        class="tii__img"
        @error="onImgError"
      >

      <!-- Empty state -->
      <div v-else class="tii__empty">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <span class="tii__empty-title">Drop image here</span>
        <span class="tii__empty-meta">or click to browse · max {{ maxSizeMb }} MB</span>
      </div>

      <!-- Uploading overlay -->
      <div v-if="uploading" class="tii__overlay tii__overlay--uploading">
        <span class="tii__spinner" aria-hidden="true"></span>
        <span>Uploading…</span>
      </div>

      <!-- Focal-point marker — only when binding is enabled and an image
           is set. Pointer-events: none so the image surface still receives
           click/pointerdown for moving the marker. The pulse ring on
           hover signals "click to move me." -->
      <div
        v-if="focalEnabled && hasImage && !uploading"
        :class="['tii__focal', { 'tii__focal--default': !focalActive }]"
        :style="{ left: focalDisplay.x + '%', top: focalDisplay.y + '%' }"
        aria-hidden="true"
      >
        <span class="tii__focal-ring"></span>
        <span class="tii__focal-dot"></span>
      </div>

      <!-- Hover-revealed actions on a preview -->
      <div v-if="hasImage && !uploading" class="tii__overlay tii__overlay--actions">
        <button
          type="button"
          class="tii__action"
          aria-label="Replace image"
          @click.stop="pickFile"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
            <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
          </svg>
          <span>Replace</span>
        </button>
        <button
          type="button"
          class="tii__action tii__action--danger"
          aria-label="Remove image"
          @click.stop="clear"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            <path d="M10 11v6M14 11v6"/>
          </svg>
          <span>Remove</span>
        </button>
      </div>
    </div>

    <!-- Footer row: focal-point status / reset, URL toggle -->
    <div class="tii__footer">
      <span v-if="focalEnabled && hasImage" class="tii__focal-hint">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9"/>
          <line x1="12" y1="3" x2="12" y2="6"/>
          <line x1="12" y1="18" x2="12" y2="21"/>
          <line x1="3" y1="12" x2="6" y2="12"/>
          <line x1="18" y1="12" x2="21" y2="12"/>
          <circle cx="12" cy="12" r="2.5"/>
        </svg>
        <span v-if="focalActive">
          Focal {{ focalPoint.x.toFixed(0) }}% · {{ focalPoint.y.toFixed(0) }}%
        </span>
        <span v-else>Click image to set focal point</span>
        <button
          v-if="focalActive"
          type="button"
          class="tii__focal-reset"
          @click="resetFocal"
        >Reset</button>
      </span>
      <button
        type="button"
        class="tii__url-toggle"
        @click="showUrlInput = !showUrlInput"
      >
        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
        </svg>
        {{ showUrlInput ? 'Hide URL' : 'Use URL instead' }}
      </button>
    </div>

    <!-- URL input -->
    <div v-if="showUrlInput" class="tii__url-row">
      <input
        type="text"
        class="tii__url-input"
        placeholder="https://… or /images/…"
        :value="modelValue"
        @input="$emit('update:modelValue', $event.target.value)"
      >
    </div>

    <!-- Error chip -->
    <div v-if="errorMessage" class="tii__error" role="alert">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <span>{{ errorMessage }}</span>
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      class="tii__file"
      @change="onFileChange"
    >
  </div>
</template>

<script>
/**
 * Polished image input for the template editor. Supports drag-and-drop,
 * click-to-browse, and a URL fallback (collapsed by default). The drop
 * zone doubles as the preview when an image is set — a hover overlay
 * surfaces Replace + Remove actions instead of stacking three buttons
 * outside it.
 *
 * Backed by POST {uploadUrl} (multipart) which returns { url }; on
 * failure the JSON error message is surfaced in the chip below the
 * zone. The parent owns the URL via v-model.
 */
export default {
  name: 'TemplateImageInput',

  props: {
    modelValue: { type: String, default: '' },
    uploadUrl: { type: String, required: true },
    subfolder: { type: String, default: null },
    label: { type: String, default: '' },
    helpText: { type: String, default: '' },
    maxSizeMb: { type: Number, default: 8 },
    /**
     * Optional focal-point binding ({ x, y } as 0–100 percentages).
     * When provided, clicking the image surface sets the focal point
     * (instead of opening the file picker — file picker still opens
     * via the hover Replace button). Null x/y means "use the default
     * defined by the consumer" — we render the marker at 50/50 in
     * that case but we don't write null→50 implicitly.
     */
    focalPoint: { type: Object, default: null },
    /**
     * Default focal point shown when the binding is null or partial.
     * Lets the consumer set a sensible default (e.g. {x: 50, y: 28}
     * for hero photos where faces sit in the upper third).
     */
    focalDefault: {
      type: Object,
      default: () => ({ x: 50, y: 50 }),
    },
  },

  emits: ['update:modelValue', 'update:focalPoint'],

  data() {
    return {
      uploading: false,
      showUrlInput: false,
      errorMessage: '',
      isDragOver: false,
      _dragDepth: 0,
      isDraggingFocal: false,
    };
  },

  computed: {
    hasImage() {
      return typeof this.modelValue === 'string' && this.modelValue.trim() !== '';
    },
    focalEnabled() {
      return this.focalPoint !== null && this.focalPoint !== undefined;
    },
    focalActive() {
      // True when admin has set a non-default focal point.
      if (!this.focalEnabled) return false;
      const { x, y } = this.focalPoint;
      return typeof x === 'number' && typeof y === 'number';
    },
    focalDisplay() {
      // Position the marker. When the binding is bound but x/y are null
      // (admin hasn't set one yet), show the default so they can see
      // where it'll start dragging from.
      if (this.focalActive) {
        return { x: this.focalPoint.x, y: this.focalPoint.y };
      }
      return this.focalDefault;
    },
  },

  methods: {
    onZoneClick(event) {
      if (this.uploading) return;
      // When focal-point picking is enabled and an image is set, the bare
      // click on the image surface sets the focal point instead of opening
      // the file picker. Replace + Remove still work via the hover overlay.
      if (this.focalEnabled && this.hasImage) {
        this.setFocalFromEvent(event);
        return;
      }
      this.pickFile();
    },

    /**
     * Translate a pointer event's coordinates inside the zone into
     * focal-point percentages and emit. Clamps to [0, 100] so a click
     * just outside the bounds doesn't produce out-of-range values
     * (can happen during a drag that leaves the box).
     */
    setFocalFromEvent(event) {
      const zone = event.currentTarget?.getBoundingClientRect
        ? event.currentTarget
        : this.$el.querySelector('.tii__zone');
      if (!zone) return;
      const rect = zone.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width;
      const py = (event.clientY - rect.top) / rect.height;
      const x = Math.max(0, Math.min(100, +(px * 100).toFixed(1)));
      const y = Math.max(0, Math.min(100, +(py * 100).toFixed(1)));
      this.$emit('update:focalPoint', { x, y });
    },

    onFocalPointerDown(event) {
      // Drag = hold left button on the image and move. Pin focal point
      // updates to the pointermove handler so the marker tracks the cursor
      // smoothly. Stop propagation so the zone's click handler doesn't
      // also fire (would re-fire setFocalFromEvent unnecessarily).
      if (!this.focalEnabled || !this.hasImage || this.uploading) return;
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.isDraggingFocal = true;
      this.setFocalFromEvent(event);
      const onMove = (e) => this.setFocalFromEvent(e);
      const onUp = () => {
        this.isDraggingFocal = false;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      // Bind move/up at window so the drag survives the cursor leaving
      // the zone — admins can flick to a corner without losing the drag.
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },

    resetFocal() {
      this.$emit('update:focalPoint', { x: null, y: null });
    },

    pickFile() {
      this.errorMessage = '';
      this.$refs.fileInput.click();
    },

    clear() {
      this.$emit('update:modelValue', '');
      this.errorMessage = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    onImgError() {
      this.errorMessage = 'Preview failed to load — check the URL.';
    },

    // Drag-and-drop tracking. Browsers fire dragenter/leave for every
    // child element the cursor enters, so we count depth instead of
    // toggling on a single event — otherwise the dragover state flickers
    // the moment the cursor crosses the icon's bounding box.
    onDragEnter() {
      this._dragDepth += 1;
      this.isDragOver = true;
    },
    onDragOver() {
      this.isDragOver = true;
    },
    onDragLeave() {
      this._dragDepth = Math.max(0, this._dragDepth - 1);
      if (this._dragDepth === 0) this.isDragOver = false;
    },
    onDrop(event) {
      this._dragDepth = 0;
      this.isDragOver = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.uploadFile(file);
    },

    async onFileChange(event) {
      const file = event.target.files && event.target.files[0];
      if (file) await this.uploadFile(file);
      event.target.value = '';
    },

    async uploadFile(file) {
      // Accept-mime guard for drag-and-drop, where the file picker's
      // accept attribute doesn't apply.
      if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
        this.errorMessage = 'Only JPG, PNG, WebP, or GIF files are allowed.';
        return;
      }
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > this.maxSizeMb) {
        this.errorMessage = `File is ${sizeMb.toFixed(1)} MB; max ${this.maxSizeMb} MB.`;
        return;
      }

      const formData = new FormData();
      formData.append('image', file);
      if (this.subfolder) formData.append('subfolder', this.subfolder);

      this.uploading = true;
      this.errorMessage = '';
      try {
        const { data } = await axios.post(this.uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data && data.url) {
          this.$emit('update:modelValue', data.url);
        } else {
          this.errorMessage = 'Upload succeeded but server returned no URL.';
        }
      } catch (err) {
        const msg = err?.response?.data?.message
          || err?.response?.data?.errors?.image?.[0]
          || err.message
          || 'Upload failed.';
        this.errorMessage = msg;
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.tii { display: flex; flex-direction: column; gap: 6px; }
.tii__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
}
.tii__help {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0 0 4px;
  line-height: 1.4;
}

/* Drop zone is the centerpiece — wide aspect, soft border by default,
   pink-accented on drag-over and active preview. */
.tii__zone {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 10px;
  border: 1.5px dashed #cbd5e1;
  background:
    repeating-linear-gradient(45deg, #f8fafc, #f8fafc 8px, #f1f5f9 8px, #f1f5f9 16px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition: border-color 160ms ease, background-color 160ms ease,
              box-shadow 200ms ease, transform 160ms cubic-bezier(0.22, 1, 0.36, 1);
}
.tii__zone:hover {
  border-color: #f9a8d4;
  background:
    repeating-linear-gradient(45deg, #fdf2f8, #fdf2f8 8px, #fce7f3 8px, #fce7f3 16px);
}
.tii__zone:focus-visible {
  border-color: #db2777;
  box-shadow: 0 0 0 3px rgba(219, 39, 119, 0.18);
}
.tii__zone--dragover {
  border-color: #db2777 !important;
  background: #fdf2f8 !important;
  transform: scale(1.005);
  box-shadow: 0 8px 24px -10px rgba(219, 39, 119, 0.35);
}
.tii__zone--has-image {
  border-style: solid;
  border-color: #e2e8f0;
  background: #0f172a;
}
.tii__zone--has-image:hover { border-color: #cbd5e1; }
.tii__zone--error {
  border-color: #fca5a5 !important;
  background: #fef2f2 !important;
}

.tii__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Empty state: dotted upload glyph + two lines of muted copy. */
.tii__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: #64748b;
  pointer-events: none;
  padding: 0 16px;
  text-align: center;
}
.tii__empty svg { color: #94a3b8; margin-bottom: 2px; }
.tii__empty-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}
.tii__empty-meta {
  font-size: 0.7rem;
  color: #94a3b8;
  letter-spacing: 0.01em;
}
.tii__zone:hover .tii__empty svg,
.tii__zone:hover .tii__empty-title { color: #db2777; }

/* Overlay slabs: uploading takes the full zone with a dim layer; the
   actions overlay reveals on hover when an image is set. */
.tii__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
}
.tii__overlay--uploading {
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(2px);
  pointer-events: none;
}
.tii__overlay--actions {
  background: linear-gradient(to top, rgba(15, 23, 42, 0.72), rgba(15, 23, 42, 0.0));
  opacity: 0;
  transition: opacity 180ms ease;
  align-items: flex-end;
  padding-bottom: 12px;
}
.tii__zone--has-image:hover .tii__overlay--actions { opacity: 1; }

.tii__action {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 11px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: background 140ms ease, border-color 140ms ease, transform 140ms ease;
}
.tii__action:hover {
  background: rgba(255, 255, 255, 0.32);
  border-color: rgba(255, 255, 255, 0.7);
  transform: translateY(-1px);
}
.tii__action:active { transform: translateY(0); }
.tii__action--danger:hover {
  background: rgba(220, 38, 38, 0.85);
  border-color: rgba(220, 38, 38, 0.95);
}

/* Spinner inherits white from the overlay text color */
.tii__spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  animation: tii-spin 0.6s linear infinite;
}
@keyframes tii-spin { to { transform: rotate(360deg); } }

/* Focal-point picker — crosshair marker the admin clicks/drags to set
   where the image's subject sits. The zone gets cursor: crosshair so
   the affordance is obvious. */
.tii__zone--focal-mode { cursor: crosshair; }
.tii__zone--focal-dragging .tii__focal { transition: none; }
.tii__focal {
  position: absolute;
  width: 28px;
  height: 28px;
  pointer-events: none;
  transform: translate(-50%, -50%);
  transition: left 200ms cubic-bezier(0.22, 1, 0.36, 1),
              top 200ms cubic-bezier(0.22, 1, 0.36, 1);
  z-index: 3;
}
.tii__focal-ring {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px rgba(219, 39, 119, 0.95),
              0 4px 12px -3px rgba(0, 0, 0, 0.45);
  background: rgba(219, 39, 119, 0.14);
  animation: tii-focal-pulse 1.6s ease-out infinite;
}
.tii__focal-dot {
  position: absolute;
  inset: 10px;
  border-radius: 999px;
  background: #db2777;
  box-shadow: 0 0 0 2px #fff;
}
.tii__focal--default .tii__focal-ring {
  /* Faded marker when admin hasn't actively set a focal point — shows
     them where the default sits without claiming it as their choice. */
  border-color: rgba(255, 255, 255, 0.7);
  box-shadow: 0 0 0 2px rgba(219, 39, 119, 0.45),
              0 3px 8px -3px rgba(0, 0, 0, 0.35);
  background: rgba(219, 39, 119, 0.06);
  animation: none;
}
.tii__focal--default .tii__focal-dot {
  background: rgba(219, 39, 119, 0.6);
}
@keyframes tii-focal-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.15); opacity: 0.85; }
}
@media (prefers-reduced-motion: reduce) {
  .tii__focal { transition: none; }
  .tii__focal-ring { animation: none; }
}

/* Footer + URL row */
.tii__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  flex-wrap: wrap;
}
.tii__focal-hint {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.7rem;
  font-weight: 500;
  color: #64748b;
  letter-spacing: 0.01em;
}
.tii__focal-hint svg { color: #db2777; flex-shrink: 0; }
.tii__focal-reset {
  appearance: none;
  background: transparent;
  border: none;
  color: #db2777;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  padding: 1px 5px;
  margin-left: 2px;
  border-radius: 3px;
}
.tii__focal-reset:hover { background: #fce7f3; }
.tii__url-toggle {
  appearance: none;
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 140ms ease, background 140ms ease;
}
.tii__url-toggle:hover { color: #db2777; background: #fdf2f8; }
.tii__url-toggle svg { color: currentColor; }

.tii__url-row { margin-top: 4px; }
.tii__url-input {
  width: 100%;
  font-size: 0.825rem;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
}
.tii__url-input:focus {
  outline: none;
  border-color: #db2777;
  box-shadow: 0 0 0 3px rgba(219, 39, 119, 0.16);
}

/* Error chip — red soft + icon */
.tii__error {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
  padding: 6px 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #991b1b;
  font-size: 0.75rem;
  line-height: 1.35;
}
.tii__error svg { color: #dc2626; flex-shrink: 0; margin-top: 1px; }

/* Hidden native input — kept in DOM for ref access */
.tii__file {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .tii__zone, .tii__action, .tii__overlay--actions { transition-duration: 80ms; }
  .tii__zone--dragover { transform: none; }
}
</style>
